import { useState } from 'react'
import { Plus, MessageSquare, Clock, CheckCircle, AlertCircle } from 'lucide-react'
import { toast } from 'react-hot-toast'

interface Query {
  id: string
  title: string
  description: string
  category: string
  status: 'pending' | 'in-progress' | 'resolved' | 'closed'
  priority: 'low' | 'medium' | 'high'
  createdAt: string
  updatedAt: string
  response?: string
}

const mockQueries: Query[] = [
  {
    id: '1',
    title: 'GST Filing Assistance',
    description: 'Need help with GST filing for March 2024 quarter. Having issues with ITC reconciliation.',
    category: 'GST',
    status: 'in-progress',
    priority: 'high',
    createdAt: '2024-02-01T10:00:00Z',
    updatedAt: '2024-02-02T14:30:00Z',
    response: 'We are reviewing your GST filing documents. Will provide detailed guidance within 24 hours.'
  },
  {
    id: '2',
    title: 'Tax Deduction Query',
    description: 'Confused about tax deductions available for business expenses. Need clarification on eligible items.',
    category: 'Tax',
    status: 'resolved',
    priority: 'medium',
    createdAt: '2024-01-28T09:15:00Z',
    updatedAt: '2024-01-30T16:45:00Z',
    response: 'Here is the complete list of eligible business deductions for your reference. Please review and let us know if you need further clarification.'
  },
  {
    id: '3',
    title: 'ITC Claim Process',
    description: 'Want to understand the process for claiming Input Tax Credit on purchases.',
    category: 'ITC',
    status: 'pending',
    priority: 'low',
    createdAt: '2024-02-03T11:20:00Z',
    updatedAt: '2024-02-03T11:20:00Z'
  }
]

export default function QueryManagement() {
  const [queries, setQueries] = useState<Query[]>(mockQueries)
  const [showNewQueryModal, setShowNewQueryModal] = useState(false)
  const [newQuery, setNewQuery] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium' as const
  })

  const categories = ['GST', 'Tax', 'ITC', 'Filing', 'Other']
  const priorities = ['low', 'medium', 'high']

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'in-progress': return 'bg-blue-100 text-blue-800'
      case 'resolved': return 'bg-green-100 text-green-800'
      case 'closed': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handleSubmitQuery = () => {
    if (!newQuery.title || !newQuery.description || !newQuery.category) {
      toast.error('Please fill in all required fields')
      return
    }

    const query: Query = {
      id: Date.now().toString(),
      title: newQuery.title,
      description: newQuery.description,
      category: newQuery.category,
      status: 'pending',
      priority: newQuery.priority,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    setQueries(prev => [query, ...prev])
    setNewQuery({
      title: '',
      description: '',
      category: '',
      priority: 'medium'
    })
    setShowNewQueryModal(false)
    toast.success('Query submitted successfully!')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Query Management</h1>
            <p className="text-gray-600 mt-2">
              Submit queries and track responses from our tax experts.
            </p>
          </div>
          <button
            onClick={() => setShowNewQueryModal(true)}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Query
          </button>
        </div>
      </div>

      {/* Query Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {queries.filter(q => q.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MessageSquare className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">
                {queries.filter(q => q.status === 'in-progress').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">
                {queries.filter(q => q.status === 'resolved').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{queries.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Queries List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Your Queries</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {queries.map(query => (
            <div key={query.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-medium text-gray-900">{query.title}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(query.status)}`}>
                      {query.status}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(query.priority)}`}>
                      {query.priority}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-3">{query.description}</p>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                    <span>Category: {query.category}</span>
                    <span>Created: {formatDate(query.createdAt)}</span>
                    <span>Updated: {formatDate(query.updatedAt)}</span>
                  </div>
                  
                  {query.response && (
                    <div className="bg-gray-50 rounded-lg p-4 mt-3">
                      <h5 className="font-medium text-gray-900 mb-2">Response:</h5>
                      <p className="text-gray-700">{query.response}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* New Query Modal */}
      {showNewQueryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Submit New Query</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={newQuery.title}
                  onChange={(e) => setNewQuery(prev => ({ ...prev, title: e.target.value }))}
                  className="input w-full"
                  placeholder="Enter query title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={newQuery.description}
                  onChange={(e) => setNewQuery(prev => ({ ...prev, description: e.target.value }))}
                  className="input w-full h-24"
                  placeholder="Describe your query in detail"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={newQuery.category}
                    onChange={(e) => setNewQuery(prev => ({ ...prev, category: e.target.value }))}
                    className="input w-full"
                  >
                    <option value="">Select category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={newQuery.priority}
                    onChange={(e) => setNewQuery(prev => ({ ...prev, priority: e.target.value as any }))}
                    className="input w-full"
                  >
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowNewQueryModal(false)}
                className="btn btn-outline flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitQuery}
                className="btn btn-primary flex-1"
              >
                Submit Query
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 